
import { useRef, useEffect, useState } from "react";
import { debugLog, DebugLevel } from "@/utils/debugUtils";

interface UseWordInputEffectsProps {
  isCurrentRound: boolean;
  autoFocus: boolean;
  userHasSubmitted: boolean;
  botIsThinking: boolean;
  wordError: string;
  onWordChange?: (word: string) => void;
  onClearWordError?: () => void;
}

export const useWordInputEffects = ({
  isCurrentRound,
  autoFocus,
  userHasSubmitted,
  botIsThinking,
  wordError,
  onWordChange,
  onClearWordError
}: UseWordInputEffectsProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showCheckmark, setShowCheckmark] = useState(false);
  const [showError, setShowError] = useState(false);
  const [localErrorMessage, setLocalErrorMessage] = useState("");
  const [isErrorProcessing, setIsErrorProcessing] = useState(false);
  
  // Track previous botIsThinking state to detect transitions
  const [prevBotThinking, setPrevBotThinking] = useState(botIsThinking);

  // More aggressive focus management - use both immediate and delayed focus
  useEffect(() => {
    if (isCurrentRound && autoFocus && inputRef.current) {
      // Try to focus immediately
      inputRef.current.focus();
      debugLog(DebugLevel.INFO, "wordInputEffects", "Initial focus attempt on input");
      
      // Also try after a short delay to ensure DOM is ready
      const focusTimer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          debugLog(DebugLevel.INFO, "wordInputEffects", "Secondary focus attempt on input");
        }
      }, 150);
      
      return () => clearTimeout(focusTimer);
    }
  }, [isCurrentRound, autoFocus]);

  // Effect to handle showing the checkmark and detect bot thinking transitions
  useEffect(() => {
    debugLog(DebugLevel.INFO, "wordInputEffects", `Checkmark effect - userHasSubmitted: ${userHasSubmitted} botIsThinking: ${botIsThinking} prevThinking: ${prevBotThinking}`);
    
    // CRITICAL FIX: Always show checkmark when user has submitted, regardless of bot state
    if (userHasSubmitted) {
      debugLog(DebugLevel.INFO, "wordInputEffects", "Should show checkmark now!");
      setShowCheckmark(true);
      
      // Detect transition from bot thinking to not thinking (while user has submitted)
      // This is a crucial moment when both words are available and ready for processing
      if (prevBotThinking && !botIsThinking && userHasSubmitted) {
        debugLog(DebugLevel.INFO, "wordInputEffects", "Detected bot finished thinking while user has submitted");
        // The checkmark will be hidden after a delay, signaling that the round is complete
        const timer = setTimeout(() => {
          setShowCheckmark(false);
          debugLog(DebugLevel.INFO, "wordInputEffects", "Hiding checkmark after bot finished");
        }, 1500);
        
        return () => clearTimeout(timer);
      }
    } else {
      setShowCheckmark(false);
    }
    
    // Update previous bot thinking state
    setPrevBotThinking(botIsThinking);
  }, [userHasSubmitted, botIsThinking, prevBotThinking]);

  // Error handling with consistent fade in/out animation and timing
  useEffect(() => {
    // Only start a new error display sequence when wordError changes from empty to something
    if (wordError && !isErrorProcessing) {
      debugLog(DebugLevel.INFO, "wordInputEffects", "New word error detected:", wordError);
      setIsErrorProcessing(true);
      setLocalErrorMessage(wordError);
      setShowError(true);
      
      // Set a timer to clear the error after 1.5 seconds
      const errorTimer = setTimeout(() => {
        debugLog(DebugLevel.INFO, "wordInputEffects", "Error timer completed - clearing error state");
        setShowError(false);
        setLocalErrorMessage("");
        
        // Clear the input field
        if (onWordChange) {
          debugLog(DebugLevel.INFO, "wordInputEffects", "Clearing input field");
          onWordChange("");
        }
        
        // Clear the error in parent component
        if (onClearWordError) {
          debugLog(DebugLevel.INFO, "wordInputEffects", "Calling parent onClearWordError");
          onClearWordError();
        }
        
        // Focus the input after clearing everything
        setTimeout(() => {
          if (inputRef.current) {
            debugLog(DebugLevel.INFO, "wordInputEffects", "Focusing input after error cleared");
            inputRef.current.focus();
          }
          // Reset error processing flag after everything is done
          setIsErrorProcessing(false);
        }, 50);
      }, 1500); // Show error for exactly 1.5 seconds
      
      return () => {
        clearTimeout(errorTimer);
        setIsErrorProcessing(false);
      };
    } else if (!wordError) {
      // Reset error processing state when wordError is cleared externally
      setIsErrorProcessing(false);
    }
  }, [wordError, onWordChange, onClearWordError]);

  return {
    inputRef,
    showCheckmark,
    showError,
    localErrorMessage,
    isErrorProcessing
  };
};
